import { Badge, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { STATUS_COLORS, type StatusTone } from '@/theme/colors';
import { brand } from '@/theme/theme';

export interface Finding {
  code?: string | null;
  severity?: string | null;
  message_key?: string | null;
  params?: Record<string, unknown> | null;
}

const TONE: Record<string, StatusTone> = { blocking: 'failed', warning: 'pending', info: 'muted' };

/** Codes whose steps send different params: the i18next context picks the matching sentence (`<key>_<context>`). */
const CONTEXT: Record<string, (params: Record<string, unknown>) => string | undefined> = {
  // preprocess-lidar-mls lists the files' CRSs instead of a declared and a detected one
  crs_mismatch: (p) => ('crs' in p && !('declared' in p) ? 'sources' : undefined),
  // a flight without a master station was searched around before giving up
  ppk_base_not_available: (p) => ('max_offset_km' in p ? 'searched' : undefined),
  // the CZEPOS virtual station stood away from the flight, which may be why few photos fixed
  ppk_low_fixed: (p) => (typeof p.base_offset_km === 'number' && p.base_offset_km > 0 ? 'offset' : undefined),
};

/** The localised sentence for a finding or rejection reason; falls back to the code. */
export function useFindingMessage() {
  const { t } = useTranslation('findings');
  return (f: Finding) => {
    const params = f.params ?? {};
    const context = CONTEXT[f.code ?? '']?.(params);
    return t(f.message_key ?? `finding.${f.code ?? ''}`, { ...params, context, defaultValue: f.code ?? f.message_key ?? '' });
  };
}

export function FindingsList({ findings, compact = false }: { findings: Finding[]; compact?: boolean }) {
  const { t } = useTranslation('runs');
  const message = useFindingMessage();
  if (findings.length === 0) {
    return (
      <Text fz={13} c={brand.textMuted}>
        {t('findings.none')}
      </Text>
    );
  }
  return (
    <Stack gap={compact ? 4 : 'xs'}>
      {findings.map((f, i) => {
        const c = STATUS_COLORS[TONE[f.severity ?? ''] ?? 'muted'];
        return (
          <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
            <Badge variant="outline" fz={11} styles={{ root: { color: c.color, backgroundColor: c.bg, borderColor: c.border, flexShrink: 0 } }}>
              {t(`findings.severity.${f.severity ?? 'info'}`, { defaultValue: f.severity ?? '' })}
            </Badge>
            <Text fz={compact ? 12 : 14}>
              {message(f)}
              {!compact && (
                <Text component="span" ff="monospace" fz={11} c={brand.textMuted}>
                  {' '}
                  {f.code}
                </Text>
              )}
            </Text>
          </Group>
        );
      })}
    </Stack>
  );
}
